// Temps réel côté interface : un seul flux d'événements envoyés par le serveur (SSE, /api/v1/flux) par
// onglet, avec reprise. Tant que la connexion est seulement coupée, EventSource se reconnecte seul et renvoie
// l'identifiant du dernier événement reçu (Last-Event-ID) ; si le flux est fermé (serveur redémarré, réseau
// perdu, erreur), un nouveau flux est ouvert avec `depuis=<dernier identifiant>`, après un délai croissant ou
// dès le retour du réseau. Dans les deux cas, le serveur rejoue d'abord les événements manqués dont
// l'utilisateur est destinataire. Chaque événement invalide les requêtes concernées : les données restent
// lues par l'API, avec ses contrôles de droits.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CLE_ETAT } from '../../app/session';

export type EtatFlux = 'connexion' | 'connecte' | 'interrompu' | 'indisponible';

const ContexteFlux = createContext<EtatFlux>('indisponible');

const EVENEMENTS_MESSAGERIE = ['message', 'message_modifie', 'message_masque', 'lecture'] as const;
const FERME = 2;

/** Délai avant une nouvelle ouverture du flux (millisecondes), croissant puis plafonné à 30 secondes. */
export function delaiReconnexion(tentative: number): number {
  return [1_000, 2_000, 5_000, 10_000, 30_000][Math.min(Math.max(tentative, 0), 4)]!;
}

/** Adresse du flux, avec reprise depuis le dernier événement reçu. */
export function urlFlux(dernierId: string | null): string {
  return dernierId ? `/api/v1/flux?depuis=${encodeURIComponent(dernierId)}` : '/api/v1/flux';
}

export type FabriqueSource = (url: string) => EventSource;

export function FournisseurTempsReel({
  actif,
  children,
  fabrique,
}: {
  actif: boolean;
  children: ReactNode;
  fabrique?: FabriqueSource;
}) {
  const client = useQueryClient();
  const [etat, setEtat] = useState<EtatFlux>(actif ? 'connexion' : 'indisponible');

  useEffect(() => {
    const creer: FabriqueSource | null =
      fabrique ?? (typeof EventSource === 'undefined' ? null : (url) => new EventSource(url));
    if (!actif || !creer) {
      setEtat('indisponible');
      return;
    }
    let source: EventSource | null = null;
    let dernierId: string | null = null;
    let tentative = 0;
    let minuterie: ReturnType<typeof setTimeout> | null = null;
    let arrete = false;

    const noter = (e: MessageEvent<string>) => {
      if (e.lastEventId) dernierId = e.lastEventId;
    };
    const lire = (e: MessageEvent<string>): Record<string, unknown> => {
      try {
        return (JSON.parse(e.data) as Record<string, unknown>) ?? {};
      } catch {
        return {};
      }
    };
    const surMessagerie = (e: MessageEvent<string>) => {
      noter(e);
      const conversationId = lire(e).conversationId;
      void client.invalidateQueries({ queryKey: ['messagerie', 'conversations'] });
      void client.invalidateQueries({ queryKey: ['messagerie', 'statut'] });
      if (typeof conversationId === 'string') {
        void client.invalidateQueries({ queryKey: ['messagerie', 'messages', conversationId] });
        void client.invalidateQueries({ queryKey: ['messagerie', 'conversation', conversationId] });
        if (e.type === 'message')
          void client.invalidateQueries({ queryKey: ['messagerie', 'decisions', conversationId] });
      }
    };
    const surNotification = (e: MessageEvent<string>) => {
      noter(e);
      void client.invalidateQueries({ queryKey: ['notifications'] });
    };
    const surReprise = (e: MessageEvent<string>) => {
      const r = lire(e);
      // Événements rejoués, ou coupure plus longue que la fenêtre de reprise : les listes sont relues.
      if (r.complete === false || (typeof r.nombre === 'number' && r.nombre > 0)) {
        void client.invalidateQueries({ queryKey: ['messagerie'] });
        void client.invalidateQueries({ queryKey: ['notifications'] });
      }
    };
    const surSession = () => {
      // Session terminée (déconnexion, révocation, suspension) : aucune reprise, retour à la connexion.
      arrete = true;
      source?.close();
      setEtat('indisponible');
      void client.invalidateQueries({ queryKey: CLE_ETAT });
    };

    const planifier = () => {
      if (arrete || minuterie) return;
      minuterie = setTimeout(() => {
        minuterie = null;
        tentative++;
        ouvrir();
      }, delaiReconnexion(tentative));
    };

    function ouvrir() {
      if (arrete) return;
      source?.close();
      const s = creer!(urlFlux(dernierId));
      source = s;
      setEtat(dernierId ? 'interrompu' : 'connexion');
      s.onopen = () => {
        tentative = 0;
        setEtat('connecte');
      };
      s.onerror = () => {
        if (source !== s) return;
        setEtat('interrompu');
        // Flux fermé : EventSource ne réessaiera plus, un nouveau flux reprend depuis le dernier événement.
        if (s.readyState === FERME) planifier();
      };
      for (const t of EVENEMENTS_MESSAGERIE) s.addEventListener(t, surMessagerie as EventListener);
      s.addEventListener('notification', surNotification as EventListener);
      s.addEventListener('notification_lue', surNotification as EventListener);
      s.addEventListener('connexion', noter as EventListener);
      s.addEventListener('reprise', surReprise as EventListener);
      s.addEventListener('session', surSession);
    }

    const surRetourReseau = () => {
      if (arrete || (source && source.readyState !== FERME)) return;
      if (minuterie) {
        clearTimeout(minuterie);
        minuterie = null;
      }
      ouvrir();
    };
    const surVisibilite = () => {
      if (document.visibilityState === 'visible') surRetourReseau();
    };
    window.addEventListener('online', surRetourReseau);
    document.addEventListener('visibilitychange', surVisibilite);
    ouvrir();
    return () => {
      arrete = true;
      if (minuterie) clearTimeout(minuterie);
      window.removeEventListener('online', surRetourReseau);
      document.removeEventListener('visibilitychange', surVisibilite);
      source?.close();
    };
  }, [actif, client, fabrique]);

  return <ContexteFlux.Provider value={etat}>{children}</ContexteFlux.Provider>;
}

export function useEtatFlux(): EtatFlux {
  return useContext(ContexteFlux);
}

export const LIBELLES_ETAT_FLUX: Record<EtatFlux, string> = {
  connexion: 'Connexion au temps réel',
  connecte: 'Temps réel actif',
  interrompu: 'Connexion interrompue, reprise automatique',
  indisponible: 'Temps réel indisponible, actualisation périodique',
};

/** Intervalle d'actualisation des listes quand le flux ne fonctionne pas (aucune actualisation sinon). */
export function intervalleSecours(etat: EtatFlux): number | false {
  return etat === 'connecte' ? false : 30_000;
}
